import { ReactNode } from 'react';

import { Grid, Typography } from '@mui/material';

import clsx from 'clsx';
import styled from 'styled-components';

import { Constants, getCDNOptimizedImage } from '@arrived/common';

import { DefaultOptions } from './DefaultOptions';
import { OptionCard, OptionCardProps } from './OptionCard';

import { NavigationArrivedLogo } from '../navigation';

const ERROR_BACKDROP_WIDTH = 524;

const StyledErrorPageContainer = styled.div`
  &.error-page-container {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    display: flex;

    .error-page-inner-container {
      position: relative;
      z-index: 1;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      margin: 0 ${ERROR_BACKDROP_WIDTH}px 0 100px;
      width: 100%;
    }

    .error-page-header {
      flex: 0 1 auto;
      display: flex;
      flex-direction: column;
      margin-top: ${({ theme }) => theme.spacing(7)};

      .error-text-container {
        display: flex;
        flex-direction: column;
        width: 60%;
        min-width: 250px;
        margin: ${({ theme }) => theme.spacing(6, 0, 0)};
        gap: ${({ theme }) => theme.spacing(1)};

        .error-text-container-sub-text {
          margin-top: ${({ theme }) => theme.spacing(3)};
        }
      }
    }

    .error-page-items {
      flex: 0 1 auto;
      margin-top: ${({ theme }) => theme.spacing(3)};
    }

    .error-page-backdrop {
      z-index: 0;
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      width: ${ERROR_BACKDROP_WIDTH}px;

      .error-page-backdrop-icon-container {
        z-index: 2;
        position: absolute;
        right: 140px;
        top: 200px;
        background-color: ${({ theme }) => theme.palette.tertiary[500]};
        color: ${({ theme }) => theme.palette.neutrals.white};
        width: 57px;
        height: 57px;
        border-radius: 50%;
        display: flex;
        justify-content: center;
        align-items: center;
      }

      .error-page-backdrop-image {
        width: 100%;
        height: 100%;
      }
    }

    a {
      color: ${({ theme }) => theme.palette.primary.main};

      &:hover {
        text-decoration: underline;
      }
    }

    ${({ theme }) => theme.breakpoints.down('md')} {
      flex-direction: column;

      .error-page-inner-container {
        margin: 0;
        padding: 0 ${({ theme }) => theme.spacing(2.5)};
        width: 100%;
      }

      .error-page-header {
        margin-top: ${({ theme }) => theme.spacing(2.5)};
        height: auto;

        .error-text-container {
          width: auto;

          .error-text-container-sub-text {
            margin-top: ${({ theme }) => theme.spacing(2.5)};
          }
        }
      }

      .error-page-backdrop {
        height: 50%;
        top: auto;
        left: 0;
      }
    }
  }
`;

interface ErrorPageProps {
  className?: string;
  titleText: ReactNode;
  subTitleText: ReactNode;
  bodyText: ReactNode;
  icon?: ReactNode;
  backdropOverride?: ReactNode;
  options?: OptionCardProps[];
}

export const ErrorPage = ({
  className,
  backdropOverride,
  titleText,
  subTitleText,
  bodyText,
  icon,
  options,
}: ErrorPageProps) => {
  const renderedOptions = options ?? DefaultOptions;

  return (
    <StyledErrorPageContainer className={clsx('error-page-container', className)}>
      <div className="error-page-inner-container">
        <div className="error-page-header">
          <NavigationArrivedLogo />
          <div className="error-text-container">
            <Typography variant="h1.semibold" className={clsx('text-container', 'error-text-container-title')}>
              {titleText}
            </Typography>
            <Typography variant="h4.semibold" className={clsx('text-container', 'error-text-container-sub-text')}>
              {subTitleText}
            </Typography>
            <Typography className={clsx('text-container', 'error-text-container-body-text')} variant="body1">
              {bodyText}
            </Typography>
          </div>
        </div>
        <Grid className="error-page-items" container rowSpacing={1.5} columnSpacing={4}>
          {renderedOptions.map((option, idx) => (
            <Grid key={`option-${idx}`} item xs={12} sm={12} md={4}>
              <OptionCard {...option} />
            </Grid>
          ))}
        </Grid>
      </div>
      <div className="error-page-backdrop">
        {icon && <span className="error-page-backdrop-icon-container">{icon}</span>}
        {backdropOverride ?? (
          <img
            src={getCDNOptimizedImage({
              imgUrl: `${Constants.awsCdnUrl}/images/painting.png`,
            })}
            alt=""
            className="error-page-backdrop-image"
          />
        )}
      </div>
    </StyledErrorPageContainer>
  );
};
